// FoodLens predict 결과 → 디자인 화면 모델 변환
// 현재는 SDK 가 리턴하는 필드만 매핑. (aiAnalysis, importantKeys 등은 하드코딩 유지)

// SDK 는 "값 없음"을 -1 로 내려보낸다. 실제 0(예: 잡곡밥 sodium 0.0)과 반드시 구분해야 한다.
// 실측 확인: bcaa, 식품군 9종(grainGroup~unhealthyOilGroup) 은 -1 로만 오고,
//            dha/epa/vitaminD 는 진짜 0 으로 온다.
const MISSING = -1;

/** 값이 실제로 제공된 수치인지 (미제공 -1 과 실제 0 을 구분) */
export const isProvided = (n) => Number.isFinite(n) && n !== MISSING;

/** 표시용 영양소 값. 미제공이면 null (화면에서 "-" 처리용), 제공되면 소수 1자리 */
export const nutrient = (n) => (isProvided(n) ? Math.round(n * 10) / 10 : null);

/** 합산용 영양소 값. 미제공이면 0. 총합 계산처럼 숫자가 강제되는 곳 전용 */
const nutrientOr0 = (n) => nutrient(n) ?? 0;

/**
 * candidate 에서 수량/단위/1인분 중량을 뽑는다.
 * countsDetail 이 구조화된 정답 소스이므로 최우선.
 *   countsDetail[0] = { countType, counts:"접시(중)", countsValue:1.0, servingSize:125.3,
 *                       subCounts:"토막", subCountsValue:1.0 }
 * 실측상 counts 는 "접시(중)"/"국그릇"/"공기(중)"/"종지" 처럼 숫자로 시작하지 않으므로
 * 문자열 파싱으로는 수량을 얻을 수 없다. countsValue 를 그대로 쓴다.
 */
const readCounts = (c) => {
    const detail = Array.isArray(c?.countsDetail) ? c.countsDetail[0] : null;

    const rawQty = detail?.countsValue;
    const qty = Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 1;

    const rawUnit = detail?.counts ?? c?.counts;
    const unit = String(rawUnit ?? '').trim() || '개';

    // 1인분 중량: countsDetail 우선, 없으면 candidate 의 servingSize
    const rawServing = Number.isFinite(detail?.servingSize) && detail.servingSize > 0
        ? detail.servingSize
        : c?.servingSize;
    const serving = Number.isFinite(rawServing) && rawServing > 0 ? rawServing : null;

    // 보조 단위 (예: 갈치구이 → "토막" 1.0). 없으면 null
    const subUnit = String(detail?.subCounts ?? '').trim() || null;
    const rawSubQty = detail?.subCountsValue;
    const subQty = Number.isFinite(rawSubQty) && rawSubQty > 0 ? rawSubQty : null;

    return {
        qty,
        unit,
        amount: serving !== null ? `${serving}${c?.unit || 'g'}` : '',
        subUnit: subUnit && subQty !== null ? subUnit : null,
        subQty: subUnit && subQty !== null ? subQty : null,
    };
};

// 표시용 qty 문자열. 예: qty=1, unit="접시(중)", amount="125.3g" → "1접시(중)(125.3g)"
const formatQty = ({ qty, unit, amount }) => {
    const head = `${qty}${unit}`;
    return amount ? `${head}(${amount})` : head;
};

/**
 * predict 결과를 디자인 모델로 매핑
 * @param {object} result - SDK predict JSON (foods[])
 * @param {string} dataUri - 표시용 이미지 base64 dataUri
 * @param {number} naturalW - 원본 이미지 픽셀 width
 * @param {number} naturalH - 원본 이미지 픽셀 height
 * @returns {{ images: array, items: array }}
 */
export const mapPredictResult = (result, dataUri, naturalW, naturalH) => {
    const foods = Array.isArray(result?.foods) ? result.foods : [];
    if (foods.length === 0) return { images: [], items: [] };

    const labels = [];
    const items = [];

    foods.forEach((food, idx) => {
        const c = food?.candidates?.[0] ?? food?.userSelected ?? {};
        const name = c.fullFoodName || c.foodName || food.fullName || food.name || `음식 ${idx + 1}`;

        // 박스 중심 + 박스 자체를 0~1 정규화 (debug 박스 시각화에서 사용)
        const pos = food?.position;
        if (pos && naturalW > 0 && naturalH > 0 && pos.xmax > pos.xmin) {
            labels.push({
                name,
                x: ((pos.xmin + pos.xmax) / 2) / naturalW,
                y: ((pos.ymin + pos.ymax) / 2) / naturalH,
                box: {
                    xmin: pos.xmin / naturalW,
                    ymin: pos.ymin / naturalH,
                    xmax: pos.xmax / naturalW,
                    ymax: pos.ymax / naturalH,
                },
            });
        }

        const counts = readCounts(c);

        // 섭취 비율. 실측은 전부 1.0 이지만 1 이 아니면 영양값에 곱해야 한다.
        const ratio = Number.isFinite(food?.eatAmount) && food.eatAmount > 0
            ? food.eatAmount
            : 1;
        const scaled = (n) => Math.round(nutrientOr0(n) * ratio * 10) / 10;

        items.push({
            id: idx + 1,
            name,
            qty: formatQty(counts),
            kcal:    scaled(c.energy),
            sodium:  scaled(c.sodium),
            protein: scaled(c.protein),
            carb:    scaled(c.carbohydrate),
            fat:     scaled(c.fat),
            // FoodItemDetail 용량 조정 기본값 (SDK countsDetail 기준)
            baseQty:    counts.qty,
            baseUnit:   counts.unit,
            baseAmount: counts.amount,
            baseSubQty:  counts.subQty,
            baseSubUnit: counts.subUnit,
        });
    });

    const images = [{
        imageUrl: dataUri,
        labels,
    }];

    return { images, items };
};
